import { useQuery } from '@tanstack/react-query';
import { Check, ExternalLink, Trash2, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApplyPanel } from '@/components/job-detail/ApplyPanel';
import { ConfirmDeleteModal } from '@/components/job-detail/ConfirmDeleteModal';
import { ResumeOrCoverTab } from '@/components/job-detail/ResumeOrCoverTab';
import { AsyncActionBtn } from '@/components/ui/AsyncActionBtn';
import { Button } from '@/components/ui/Button';
import { HoverTip } from '@/components/ui/HoverTip';
import { Markdown } from '@/components/ui/Markdown';
import { ScoreRing } from '@/components/ui/ScoreRing';
import { ScrollPane } from '@/components/ui/ScrollPane';
import { Separator } from '@/components/ui/Separator';
import { StatusPill, type StatusPillStatus } from '@/components/ui/StatusPill';
import { SubScoreBar } from '@/components/ui/SubScoreBar';
import { Tabs } from '@/components/ui/Tabs';
import { TechChips } from '@/components/ui/TechChips';
import { useApplicationAction } from '@/hooks/useApplicationAction';
import { useDeleteJob } from '@/hooks/useDeleteJob';
import { useRetailor } from '@/hooks/useRetailor';
import { useScoreJob } from '@/hooks/useScoreJob';
import { jobDetailQueryOptions } from '@/lib/api';
import {
  breakdownFromJob,
  parseMatchReasoning,
  parseTechStack,
  postedDaysAgo,
  relativeDateFromPostedAt,
} from '@/lib/jobAdapter';
import { formatSalary, scoreTier } from '@/lib/score';
import { useJobSelection } from '@/providers/dashboard/JobBrowserContext';

type Tab = 'overview' | 'jd' | 'resume' | 'cover' | 'apply';

export const JobDetail = () => {
  const { selectedId, select } = useJobSelection();
  const { data: job } = useQuery(jobDetailQueryOptions(selectedId));
  const [tab, setTab] = useState<Tab>('overview');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Track previous selectedId as state so we can reset tab/modal during render
  // (avoids cascading renders that an effect would trigger).
  const [lastSelectedId, setLastSelectedId] = useState(selectedId);
  if (lastSelectedId !== selectedId) {
    setLastSelectedId(selectedId);
    setTab('overview');
    setConfirmDelete(false);
  }

  const applicationAction = useApplicationAction();
  const deleteJob = useDeleteJob();
  const scoreJob = useScoreJob();
  const retailor = useRetailor();

  // Esc closes the confirm modal before it would close the panel.
  useEffect(() => {
    if (!confirmDelete) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setConfirmDelete(false);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [confirmDelete]);

  // Tab keyboard shortcuts: 1=Overview, 2=Description, 3=Resume, 4=Cover,
  // 5=Apply.
  useEffect(() => {
    if (!job) return;
    const handler = (event: KeyboardEvent) => {
      if (confirmDelete) return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const map: Record<string, Tab> = {
        '1': 'overview',
        '2': 'jd',
        '3': 'resume',
        '4': 'cover',
        '5': 'apply',
      };
      const next = map[event.key];
      if (!next) return;
      if (next === 'resume' && !job.tailoredResumeMd && !job.resumePdfPath)
        return;
      if (next === 'cover' && !job.coverLetter && !job.coverLetterPdfPath)
        return;
      if (
        next === 'apply'
        && job.status !== 'approved'
        && job.status !== 'applied'
        && job.appStatus !== 'approved'
        && job.appStatus !== 'submitted'
      )
        return;
      setTab(next);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [job, confirmDelete]);

  if (selectedId == null || !job)
    return (
      <div className="flex h-full items-center justify-center bg-surface text-base text-foreground-muted">
        Loading…
      </div>
    );

  const jobId = selectedId;

  const reasoning = parseMatchReasoning(job.matchReasoning);
  const breakdown = breakdownFromJob(job);
  const tech = parseTechStack(job.techStack);
  const salary = formatSalary(job.salaryMin, job.salaryMax);
  const days = postedDaysAgo(job.postedAt);
  const relativeDate = relativeDateFromPostedAt(job.postedAt);
  const jobStatus = job.status as StatusPillStatus;
  const appStatus = job.appStatus ? (job.appStatus as StatusPillStatus) : null;

  const hasResume = Boolean(job.tailoredResumeMd || job.resumePdfPath);
  const hasCover = Boolean(job.coverLetter || job.coverLetterPdfPath);

  const tabs: { id: Tab; label: string; kbd: string; visible: boolean }[] = [
    { id: 'overview', kbd: '1', label: 'Overview', visible: true },
    { id: 'jd', kbd: '2', label: 'Description', visible: true },
    { id: 'resume', kbd: '3', label: 'Tailored Resume', visible: hasResume },
    { id: 'cover', kbd: '4', label: 'Cover Letter', visible: hasCover },
    {
      id: 'apply',
      kbd: '5',
      label: 'Apply',
      visible:
        job.status === 'approved'
        || job.status === 'applied'
        || job.appStatus === 'approved'
        || job.appStatus === 'submitted',
    },
  ];

  const handleApprove = () => {
    if (job.applicationId == null) return;
    applicationAction.mutate({
      action: 'approve',
      applicationId: job.applicationId,
    });
  };

  const handleReject = () => {
    if (job.applicationId == null) return;
    applicationAction.mutate({
      action: 'reject',
      applicationId: job.applicationId,
    });
  };

  const handleClose = () => select(null);

  const handleDelete = () => {
    deleteJob.mutate(jobId, {
      onSuccess: () => {
        setConfirmDelete(false);
        handleClose();
      },
    });
  };

  const handleScore = () => scoreJob.mutateAsync(jobId);
  const handleRetailor = () => retailor.mutateAsync(jobId);

  return (
    <aside className="flex size-full animate-fadeIn flex-col border-l border-border bg-surface shadow-2xl">
      {/* Header — action menu */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <HoverTip align="start" delay={250} text="Close panel (Esc)">
          <button
            aria-label="Close panel"
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-foreground-muted transition-colors hover:bg-border/40 hover:text-foreground"
            onClick={handleClose}
            type="button"
          >
            <X className="size-3.5" />
          </button>
        </HoverTip>
        <div className="flex shrink-0 items-center gap-1.5">
          <HoverTip delay={250} text="View original job post">
            <a
              aria-label="View original job post"
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-transparent px-2.5 text-sm text-foreground transition-colors hover:bg-surface"
              href={job.url}
              rel="noopener"
              target="_blank"
            >
              <ExternalLink className="size-3.5" />
            </a>
          </HoverTip>
          {job.status === 'ready' && job.applicationId != null && (
            <>
              <Button
                kbd="A"
                onClick={handleApprove}
                size="sm"
                variant="success"
              >
                <Check className="size-3.5" /> Approve
              </Button>
              <Button kbd="X" onClick={handleReject} size="sm" variant="danger">
                <X className="size-3.5" /> Reject
              </Button>
            </>
          )}
          <HoverTip align="end" delay={250} text="Delete job">
            <Button
              aria-label="Delete job"
              onClick={() => setConfirmDelete(true)}
              size="sm"
              variant="ghost"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </HoverTip>
        </div>
      </div>
      <Separator />

      {/* Title + meta */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-start justify-between gap-4">
          <h1 className="min-w-0 flex-1 text-xl/tight font-semibold text-foreground">
            {job.title}
          </h1>
          <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
            <StatusPill kind="job" status={jobStatus} />
            {appStatus && <StatusPill kind="app" status={appStatus} />}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          <span className="font-medium">{job.company}</span>
          {job.location && (
            <span className="text-subtle text-xs">{job.location}</span>
          )}
          {salary && (
            <span className="font-mono text-score-high tabular-nums text-xs">
              {salary}
            </span>
          )}
          <span className="font-mono text-2xs tracking-wide text-subtle uppercase">
            via {job.source}
          </span>
          {relativeDate && (
            <span className="text-subtle text-2xs">{relativeDate}</span>
          )}
        </div>
        {tech.length > 0 && (
          <div className="mt-3">
            <TechChips max={12} tech={tech} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-5">
        <Tabs
          items={tabs.map((t) => ({
            badge: (
              <span className="inline-flex min-w-4 items-center justify-center rounded-sm border border-b-2 border-border bg-surface px-1 py-px font-mono text-xs font-medium text-foreground-muted opacity-60">
                {t.kbd}
              </span>
            ),
            id: t.id,
            label: t.label,
            visible: t.visible,
          }))}
          layout="inline"
          onChange={setTab}
          value={tab}
        />
      </div>

      {/* Tab content */}
      <ScrollPane className="min-h-0 flex-1" viewportClassName="px-5 py-4">
        {tab === 'overview' && (
          <div className="space-y-5">
            {/* Headline score */}
            {breakdown && reasoning ? (
              <div className="rounded-lg border border-border bg-surface px-5 py-4">
                <div className="flex items-start gap-5">
                  <ScoreRing
                    score={job.matchScore}
                    size="lg"
                    tier={scoreTier(job.matchScore)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs tracking-[0.14em] text-subtle uppercase">
                      Match reasoning · claude haiku 4.5
                    </p>
                    <p className="mt-1.5 text-sm/relaxed text-balance text-foreground">
                      {reasoning.explanation}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5">
                  <SubScoreBar
                    info="How well the job title, level, and stated responsibilities match your target roles and experience. Max 25."
                    label="Role fit"
                    value={reasoning.roleFit}
                  />
                  <SubScoreBar
                    info="Overlap between the job's required tech stack and the skills on your profile. Languages and frameworks both count. Max 25."
                    label="Tech match"
                    value={reasoning.techMatch}
                  />
                  <SubScoreBar
                    info="How the salary band compares to your declared minimum and target. Roles with no salary listed get partial credit. Max 25."
                    label="Compensation"
                    value={reasoning.compensation}
                  />
                  <SubScoreBar
                    info="Location, timezone, remote policy, on-call expectations, and other practical constraints versus your preferences. Max 25."
                    label="Logistics"
                    value={reasoning.logistics}
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-surface/40 px-5 py-8 text-center">
                <p className="text-base text-foreground-muted">
                  No score yet. Run scoring to evaluate this match.
                </p>
                <AsyncActionBtn
                  animation="pulse"
                  className="mt-3"
                  icon={Zap}
                  onAction={handleScore}
                  size="sm"
                  variant="soft"
                >
                  Score now
                </AsyncActionBtn>
              </div>
            )}

            {/* Highlights / red flags */}
            {reasoning && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-md border border-score-high/20 bg-score-high/5 p-3">
                  <p className="mb-2 font-mono text-xs tracking-wider text-score-high uppercase">
                    Highlights
                  </p>
                  {reasoning.highlights.length === 0 ? (
                    <p className="text-sm text-foreground-muted italic">
                      None noted.
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {reasoning.highlights.map((highlight) => (
                        <li
                          className="flex gap-1.5 text-sm text-foreground/90"
                          key={highlight}
                        >
                          <span className="text-score-high">+</span> {highlight}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-md border border-score-low/20 bg-score-low/5 p-3">
                  <p className="mb-2 font-mono text-xs tracking-wider text-score-low uppercase">
                    Red flags
                  </p>
                  {reasoning.redFlags.length === 0 ? (
                    <p className="text-sm text-foreground-muted italic">
                      None flagged.
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {reasoning.redFlags.map((flag) => (
                        <li
                          className="flex gap-1.5 text-sm text-foreground/90"
                          key={flag}
                        >
                          <span className="text-score-low">−</span> {flag}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Quick meta grid */}
            <div className="rounded-md border border-border bg-surface/60 p-4">
              <p className="mb-3 font-mono text-xs tracking-wider text-subtle uppercase">
                Job metadata
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {(
                  [
                    ['Source', job.source],
                    [
                      'Posted',
                      days == null
                        ? '—'
                        : days === 0
                          ? 'today'
                          : `${days} days ago`,
                    ],
                    ['Location', job.location ?? '—'],
                    ['Salary', salary ?? '—'],
                    ['Status', job.status],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    className="flex justify-between gap-3 border-b border-border/60 py-1"
                    key={key}
                  >
                    <dt className="text-foreground-muted">{key}</dt>
                    <dd className="text-right font-mono text-foreground tabular-nums">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {tab === 'jd' && (
          <div>
            {job.descriptionMd ? (
              <Markdown content={job.descriptionMd} />
            ) : job.description ? (
              <pre className="text-sm/relaxed whitespace-pre-wrap text-foreground/90">
                {job.description}
              </pre>
            ) : (
              <p className="text-base text-foreground-muted">
                No description available.
              </p>
            )}
          </div>
        )}

        {tab === 'resume' && hasResume && (
          <ResumeOrCoverTab
            applicationId={job.applicationId}
            company={job.company}
            kind="resume"
            markdown={job.tailoredResumeMd ?? null}
            onRetailor={handleRetailor}
            pdfPath={job.resumePdfPath ?? null}
            subtitle="generated by claude sonnet 4.6"
          />
        )}

        {tab === 'cover' && hasCover && (
          <ResumeOrCoverTab
            applicationId={job.applicationId}
            company={job.company}
            kind="cover"
            markdown={job.coverLetter ?? null}
            onRetailor={handleRetailor}
            pdfPath={job.coverLetterPdfPath ?? null}
          />
        )}

        {tab === 'apply' && job.applicationId != null && (
          <ApplyPanel
            applicationId={job.applicationId}
            appStatus={job.appStatus ?? null}
            jobStatus={job.status}
          />
        )}
      </ScrollPane>

      {/* Delete confirmation */}
      {confirmDelete && (
        <ConfirmDeleteModal
          company={job.company}
          hasApplication={job.applicationId != null}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          title={job.title}
        />
      )}
    </aside>
  );
};
